import React, { useState } from 'react';
import { Lead, AnomalyType, isUnlicensedState, isMadisonState, getEffectiveAnomalies } from '../../data/mockLeads';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { CampaignDripModal } from './CampaignDripModal';
import { EnrollCampaignModal } from './EnrollCampaignModal';
import { ChangeOwnerModal } from './ChangeOwnerModal';
import { RowActionsMenu } from './RowActionsMenu';
import { formatShortDate } from '../../lib/utils';
import { Megaphone, ArrowUpDown, ArrowUp, ArrowDown, Check, X as XIcon, UserCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
interface LeadTableProps {
  leads: Lead[];
  onEnrollCampaign: (leadId: string, campaignName: string) => void;
  onChangeOwner: (leadId: string, newOwner: string) => void;
}
type SortField = 'name' | 'createdDate' | 'owner' | 'loanPurpose' | 'status';
type SortDirection = 'asc' | 'desc';
// Placeholder for Salesforce Lead record navigation. Wire to LWC NavigationMixin
// or window.location later.
function navigateToLeadRecord(leadId: string) {
  // TODO: integrate with Salesforce NavigationMixin
  // e.g. this[NavigationMixin.Navigate]({ type: 'standard__recordPage', attributes: { recordId: leadId, objectApiName: 'Lead', actionName: 'view' } })
  console.log('[navigate] Lead record:', leadId);
}
function CheckIndicator({
  checked,
  label
}: {
  checked: boolean;
  label: string;
}) {
  return <div className="flex items-center gap-1.5 text-xs" data-id="element-167">
      <span className={cn('inline-flex h-3.5 w-3.5 items-center justify-center rounded border', checked ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-300 text-slate-300')} aria-hidden="true" data-id="element-168">
        {checked ? <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-169" /> : <XIcon className="w-2.5 h-2.5" strokeWidth={2.5} data-id="element-170" />}
      </span>
      <span className={cn('text-[11px] leading-tight', checked ? 'text-slate-700' : 'text-slate-400')} data-id="element-171">
        {label}
      </span>
    </div>;
}
export function LeadTable({
  leads,
  onEnrollCampaign,
  onChangeOwner
}: LeadTableProps) {
  const [sortField, setSortField] = useState<SortField>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [enrollLead, setEnrollLead] = useState<Lead | null>(null);
  const [changeOwnerLead, setChangeOwnerLead] = useState<Lead | null>(null);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedLeads = [...leads].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-400" data-id="element-172" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-600" data-id="element-173" /> : <ArrowDown className="w-3 h-3 ml-1 text-indigo-600" data-id="element-174" />;
  };
  const renderAnomalyBadges = (anomalies: AnomalyType[]) => {
    return <div className="flex flex-wrap gap-1" data-id="element-175">
        {anomalies.map((anomaly, idx) => {
        if (anomaly === 'No Issues') return <Badge key={idx} variant="success" data-id="element-176">
                No Issues
              </Badge>;
        if (anomaly === 'Round Robin Queue') return <Badge key={idx} variant="warning" data-id="element-177">
                Queue Stuck
              </Badge>;
        if (anomaly === 'Missing Campaign') return <Badge key={idx} variant="alert" data-id="element-178">
                Missing Campaign
              </Badge>;
        if (anomaly === 'Madison Handoff Incomplete') return <Badge key={idx} variant="alert" data-id="element-179">
                Madison Handoff Incomplete
              </Badge>;
        return null;
      })}
      </div>;
  };
  const getRowClassName = (anomalies: AnomalyType[]) => {
    if (anomalies.includes('Missing Campaign') || anomalies.includes('Madison Handoff Incomplete')) {
      return 'bg-rose-50/30 border-l-2 border-l-rose-400 hover:bg-rose-50/60';
    }
    if (anomalies.includes('Round Robin Queue')) {
      return 'bg-amber-50/30 border-l-2 border-l-amber-400 hover:bg-amber-50/60';
    }
    return 'border-l-2 border-l-transparent hover:bg-slate-50';
  };
  return <>
      <Card className="overflow-hidden" data-id="element-180">
        <div className="overflow-x-auto" data-id="element-181">
          <table className="w-full text-sm text-left" data-id="element-182">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50/80 border-b border-slate-200" data-id="element-183">
              <tr data-id="element-184">
                <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('name')} data-id="element-185">
                  <div className="flex items-center" data-id="element-186">
                    Lead Name <SortIcon field="name" data-id="element-187" />
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('createdDate')} data-id="element-188">
                  <div className="flex items-center" data-id="element-189">
                    Created <SortIcon field="createdDate" data-id="element-190" />
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('owner')} data-id="element-191">
                  <div className="flex items-center" data-id="element-192">
                    Owner <SortIcon field="owner" data-id="element-193" />
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('loanPurpose')} data-id="element-194">
                  <div className="flex items-center" data-id="element-195">
                    Purpose <SortIcon field="loanPurpose" data-id="element-196" />
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('status')} data-id="element-197">
                  <div className="flex items-center" data-id="element-198">
                    Status <SortIcon field="status" data-id="element-199" />
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold" data-id="element-200">Source</th>
                <th className="px-4 py-3 font-semibold" data-id="element-201">Property State</th>
                <th className="px-4 py-3 font-semibold min-w-[200px]" data-id="element-202">
                  Campaign Enrollment
                </th>
                <th className="px-4 py-3 font-semibold min-w-[140px]" data-id="element-203">
                  Anomalies
                </th>
                <th className="px-4 py-3 font-semibold text-right" data-id="element-204">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-205">
              {sortedLeads.map(lead => <tr key={lead.id} className={cn('transition-colors', getRowClassName(getEffectiveAnomalies(lead)))} data-id="element-206">
                  <td className="px-4 py-3 whitespace-nowrap" data-id="element-207">
                    <button type="button" onClick={() => navigateToLeadRecord(lead.id)} className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus-visible:underline" data-id="element-208">
                      {lead.name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-209">
                    {formatShortDate(lead.createdDate)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-id="element-210">
                    {isMadisonState(lead.propertyState) ? <div className="flex flex-col gap-1.5" data-id="element-211">
                        <Badge variant="default" className="bg-violet-50 text-violet-700 border border-violet-200/60" data-id="element-212">
                          Madison Queue
                        </Badge>
                        <div className="flex flex-col gap-0.5" data-id="element-213">
                          <CheckIndicator checked={lead.sendToMadison} label="Send to Madison" data-id="element-214" />
                          <CheckIndicator checked={lead.madisonHandoffSmsSent} label="Madison Handoff SMS Sent" data-id="element-215" />
                        </div>
                      </div> : lead.owner === 'Round Robin Queue' ? <Badge variant="warning" data-id="element-216">Round Robin Queue</Badge> : <span className="text-slate-700" data-id="element-217">{lead.owner}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-218">
                    {lead.loanPurpose}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-219">
                    {lead.status}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-220">
                    {lead.source}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-id="element-221">
                    {isUnlicensedState(lead.propertyState) ? <Badge variant="alert" title="Property is in a non-licensed state" data-id="element-222">
                        {lead.propertyState} · Unlicensed
                      </Badge> : <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200/60" data-id="element-223">
                        {lead.propertyState}
                      </span>}
                  </td>
                  <td className="px-4 py-3" data-id="element-224">
                    {lead.campaignEnrollment.enrolled ? <div className="flex flex-col gap-1" data-id="element-225">
                        <span className="font-medium text-slate-800" data-id="element-226">
                          {lead.campaignEnrollment.campaignName}
                        </span>
                        <div className="flex items-center gap-2" data-id="element-227">
                          <Badge variant="blue" data-id="element-228">
                            {lead.campaignEnrollment.status}
                          </Badge>
                          <span className="text-xs text-slate-500" data-id="element-229">
                            {lead.campaignEnrollment.enrolledDate && formatShortDate(lead.campaignEnrollment.enrolledDate)}
                          </span>
                        </div>
                      </div> : <div className="flex flex-col gap-1" data-id="element-230">
                        <span className="text-rose-600 font-medium" data-id="element-231">
                          Not enrolled
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-232">
                          Expected: {lead.campaignEnrollment.expectedCampaign}
                        </span>
                      </div>}
                  </td>
                  <td className="px-4 py-3" data-id="element-233">
                    {renderAnomalyBadges(getEffectiveAnomalies(lead))}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-id="element-234">
                    <div className="flex items-center justify-end gap-1" data-id="element-235">
                      {!isMadisonState(lead.propertyState) && <Button variant="ghost" size="sm" onClick={() => setActiveLead(lead)} title="View campaign and drip schedule" aria-label={`View campaign and drip schedule for ${lead.name}`} className="text-slate-500 hover:text-indigo-600" data-id="element-236">
                          <Megaphone className="w-4 h-4 mr-1.5" data-id="element-237" />
                          View Campaign
                        </Button>}
                      <RowActionsMenu ariaLabel={`More actions for ${lead.name}`} actions={[...(!isMadisonState(lead.propertyState) ? [{
                    label: lead.campaignEnrollment.enrolled ? 'Change campaign' : 'Enroll in campaign',
                    icon: <Megaphone className="w-4 h-4" data-id="element-239" />,
                    onClick: () => setEnrollLead(lead)
                  }] : []), {
                    label: 'Change owner',
                    icon: <UserCheck className="w-4 h-4" data-id="element-240" />,
                    onClick: () => setChangeOwnerLead(lead)
                  }]} data-id="element-238" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50 text-xs text-slate-500 flex justify-between items-center" data-id="element-241">
          <span data-id="element-242">
            Showing {sortedLeads.length} of {leads.length} leads
          </span>
        </div>
      </Card>

      <CampaignDripModal lead={activeLead} open={!!activeLead} onClose={() => setActiveLead(null)} data-id="element-243" />
      <EnrollCampaignModal lead={enrollLead} open={!!enrollLead} onClose={() => setEnrollLead(null)} onConfirm={onEnrollCampaign} data-id="element-244" />
      <ChangeOwnerModal lead={changeOwnerLead} open={!!changeOwnerLead} onClose={() => setChangeOwnerLead(null)} onConfirm={onChangeOwner} data-id="element-245" />
    </>;
}